import React, { useEffect, useRef, useState } from 'react'

/**
 * The instruction line every handheld screen leads with, plus the typed
 * fallback for a label that will not read.
 *
 * The placeholder names the thing this step wants — an inventory code or a
 * locator code — because "a code" is two different things depending on where
 * you are in the flow, and the operator cannot tell which from the box.
 *
 * The fallback is a real form field, which is exactly why the global scan
 * listener ignores keystrokes aimed at one — an operator can put the caret in
 * here and use the gun, and the characters land in the box instead of the
 * buffer. Same result either way.
 *
 * It focuses itself on every step so the caret is always waiting, and carries
 * `inputMode="none"` so focus does not summon the on-screen keyboard: half a
 * 4" panel disappearing under a keyboard nobody asked for is worse than no
 * focus at all. Touching the box switches the mode to text, so tapping it to
 * type by hand still brings the keyboard up as expected.
 */
// Long enough for a route change and its images to settle, short enough that
// nobody gets to the box first.
const FOCUS_RETRY_MS = 300

export default function ScanPrompt({ prompt, hint, placeholder, onSubmit, disabled = false }) {
    const [typed, setTyped] = useState('')
    // 'none' keeps the soft keyboard down while still accepting the scanner.
    const [keyboard, setKeyboard] = useState('none')
    const inputRef = useRef(null)

    // Re-focus whenever the step changes, so the caret follows the flow from
    // item to location without anyone reaching for the box.
    //
    // Android Chrome drops a focus() made in the same tick as a route change,
    // and a single deferred attempt still lost the race on the device. So it
    // asks more than once: after the navigation has painted, again once the
    // screen has settled, and again if a tap lands anywhere that is not itself
    // a field. Refocusing is idempotent and invisible when it is already
    // focused, so the repetition costs nothing.
    useEffect(() => {
        if (disabled) return undefined

        const focus = () => inputRef.current?.focus({ preventScroll: true })
        const frame = requestAnimationFrame(focus)
        const settled = setTimeout(focus, FOCUS_RETRY_MS)

        function onTapElsewhere(event) {
            if (event.target?.closest?.('input, textarea, select, button, a')) return
            focus()
        }
        document.addEventListener('pointerup', onTapElsewhere)

        return () => {
            cancelAnimationFrame(frame)
            clearTimeout(settled)
            document.removeEventListener('pointerup', onTapElsewhere)
        }
    }, [prompt, disabled])

    function submit(event) {
        event.preventDefault()
        const value = typed.trim()
        if (!value) return
        setTyped('')
        // Drop back to scanner mode so the keyboard does not linger between
        // scans, and keep the caret here for the next one.
        setKeyboard('none')
        inputRef.current?.focus({ preventScroll: true })
        onSubmit(value)
    }

    return (
        <>
            <div className="pda-prompt">{prompt}</div>
            {hint && <div className="pda-sub">{hint}</div>}
            <form className="pda-manual" onSubmit={submit}>
                <input
                    ref={inputRef}
                    autoFocus
                    inputMode={keyboard}
                    onPointerDown={() => setKeyboard('text')}
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    placeholder={placeholder}
                    aria-label="Type or scan a code"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    enterKeyHint="go"
                    disabled={disabled}
                />
                <button type="submit" disabled={disabled}>Go</button>
            </form>
        </>
    )
}
